import { Component, createRef } from "react";
import { withTranslation } from "react-i18next";
import {
	Paper,
	CircularProgress,
	List,
	ListItemButton,
	ListItemText,
} from "@mui/material";
// cmp
import withNavigateAndParams from "../withNavigateAndParams";
import TableEditorFilter from "./TableEditorFilter";
import TableEditorActions from "./TableEditorActions";
import TableEditorAdvanced from "./TableEditorAdvanced";
import TableEditorRight from "./TableEditorRight";
import AddNewEntryDialog from "./AddNewEntryDialog";
import DeleteDialog from "./DeleteDialog";
// services
import Gupport from "../../services/gupport";
import Responsive from "../../services/responsive";
import { Storage, StorageKeys } from "../../services/storage";
import { compareObjects } from "../../services/utils";
// types
import type { WithTranslation } from "react-i18next";
import type { ReactRouterProps } from "../../types/misc";
import type { TableNameId, CmdGetTable, CmdUpdateTable, CmdDeleteTable } from "../../types/gupport";

type Props = Readonly<WithTranslation & ReactRouterProps & {
	tableId: TableNameId;
	hasWriteAccess: boolean;
}>;

type State = {
	loading: boolean;
	error: any;
	tableItems: any;
	rocIdPrefixes: any;
	tableItemSelected: any | null;
	open: boolean;
	newKey: string;
	newKeyType: string;
	isDuplicate: boolean;
	filterValue: string;
	searchByRegex: boolean;
	searchData: boolean;
	showRemoveDialog: boolean;
	containerHeight: number;
};

const ROCID_PREFIXES = ["FLEXP", "GARDENA", "HUE", "INDEGO", "NUKI", "ROC", "TADO", "TUYA", "ZW"] as const;

class TableEditor extends Component<Props, State> {

	#refContainerElement = createRef<HTMLDivElement>();

	constructor(props: Props) {
		super(props);

		this.state = {
			loading: true,
			error: null,
			tableItems: [],
			rocIdPrefixes: {},
			tableItemSelected: null,
			open: false,
			newKey: "",
			newKeyType: "",
			isDuplicate: false,
			filterValue: "",
			searchByRegex: Storage.get(StorageKeys.metadataEditorSearchByRegex) ?? false,
			searchData: Storage.get(StorageKeys.metadataEditorSearchAll) ?? true,
			showRemoveDialog: false,
			containerHeight: 200,
		};

		this.handleScreenSizeChanged = this.handleScreenSizeChanged.bind(this);
		this.handleOpenDialogClick = this.handleOpenDialogClick.bind(this);
		this.handleDialogCloseClick = this.handleDialogCloseClick.bind(this);
		this.handleAddClick = this.handleAddClick.bind(this);
		this.handleDeleteClick = this.handleDeleteClick.bind(this);
		this.handleRemoveClick = this.handleRemoveClick.bind(this);
		this.handleFilterChange = this.handleFilterChange.bind(this);
		this.checkForExistingKeys = this.checkForExistingKeys.bind(this);
		this.handleSearchByRegexToggle = this.handleSearchByRegexToggle.bind(this);
		this.handleSearchInDataToggle = this.handleSearchInDataToggle.bind(this);
		this.hanldeTableItemUpdate = this.hanldeTableItemUpdate.bind(this);
		this.openRemoveDialog = this.openRemoveDialog.bind(this);
	}

	override componentDidMount() {
		Responsive.on("screenSizeChanged", this.handleScreenSizeChanged);

		this.handleScreenSizeChanged();
		this.fetchTable();
	}

	override componentDidUpdate(prevProps) {
		if (this.props.tableId !== prevProps.tableId) {
			this.setState({
				loading: true,
				error: null,
				tableItems: [],
				rocIdPrefixes: {},
				tableItemSelected: null,
				open: false,
				newKey: "",
				newKeyType: "",
			}, () => {
				this.fetchTable();
			});
		}
	}

	override componentWillUnmount() {
		Responsive.off("screenSizeChanged", this.handleScreenSizeChanged);
	}

	handleScreenSizeChanged() {
		if (this.#refContainerElement.current) {
			const height = window.innerHeight - this.#refContainerElement.current.getBoundingClientRect().top;
			this.setState({
				containerHeight: height - 48,
			});
		}
	}

	openRemoveDialog() {
		this.setState({
			showRemoveDialog: true,
		});
	}

	static sortTableItems(tableItems: Array<any>) {
		return tableItems.toSorted((aItem, bItem) => {
			if (aItem.id.string_key && bItem.id.string_key) {
				const aText = aItem.id.string_key.toLowerCase();
				const bText = bItem.id.string_key.toLowerCase();
				return (aText > bText) - (bText > aText);
			} else {
				const aText = JSON.stringify(aItem.id).toLowerCase();
				const bText = JSON.stringify(bItem.id).toLowerCase();
				return (aText > bText) - (bText > aText);
			}
		});
	}

	getValidKey(newKey) {
		const prefix = Object.keys(this.state.rocIdPrefixes).find((prefix) => (newKey.startsWith(prefix)));
		if (prefix) {
			return this.state.rocIdPrefixes[prefix];
		} else if (!Number.isNaN(Number(newKey))) {
			return this.state.rocIdPrefixes.DEFAULT;
		}
		return newKey;
	}

	getRocIdPrefix(tableItems) {
		const prefixes = {};
		tableItems.forEach((tableItem) => {
			let keyNumber = Number(tableItem.id.string_key);
			let prefix = "DEFAULT";
			const rocIdPrefix = ROCID_PREFIXES.find((rocIdPrefix) => (tableItem.id.string_key.startsWith(rocIdPrefix)));
			if (rocIdPrefix) {
				keyNumber = Number(tableItem.id.string_key.substring(rocIdPrefix.length));
				prefix = rocIdPrefix;
			}
			if (!Number.isNaN(keyNumber)) {
				if (!prefixes.hasOwnProperty(prefix)) {
					prefixes[prefix] = [];
				}
				prefixes[prefix].push(keyNumber);
			}
		});
		const rocIdPrefixes = {};
		Object.keys(prefixes).forEach((prefix) => {
			const newNumber = (Math.max(...prefixes[prefix]) + 1).toString().padStart(4, "0");
			if (prefix === "DEFAULT") {
				rocIdPrefixes[prefix] = newNumber;
			} else {
				rocIdPrefixes[prefix] = prefix + newNumber;
			}
		});
		return rocIdPrefixes;
	}

	fetchTable() {
		const cmd = {
			action: "getTable",
			tableName: this.props.tableId,
		} as const satisfies CmdGetTable;
		Gupport.send(cmd, (error, msg) => {
			if (!error && msg?.payload.status === "ok") {
				const tableItems = TableEditor.sortTableItems(msg.payload.data);
				this.setState({
					loading: false,
					error: null,
					tableItems: tableItems,
					filterValue: (this.props.tableId === "rocid_dict") ? (Storage.get(StorageKeys.rocIdFilterQuery) || "") : "",
					rocIdPrefixes: (this.props.tableId === "rocid_dict") ? this.getRocIdPrefix(tableItems) : {},
				});
			} else {
				this.setState({
					loading: false,
					error: error ?? msg?.payload.data,
					tableItems: [],
					rocIdPrefixes: {},
				});
			}
		});
	}

	hanldeTableItemUpdate(data) {
		const _tableItems = TableEditor.sortTableItems(data);
		const _tableItem = _tableItems.find((tableItem) => (JSON.stringify(tableItem.id) === JSON.stringify(this.state.tableItemSelected?.id)));
		this.setState({
			tableItemSelected: _tableItem,
			tableItems: _tableItems,
		});
	}

	handleSelectableListChange(tableItem) {
		this.setState({
			tableItemSelected: tableItem,
		});
	}

	checkForExistingKeys(key) {
		return this.state.tableItems.some((tableItem) => {
			if (tableItem.id.string_key) {
				return tableItem.id.string_key.toLowerCase() === key.toLowerCase();
			} else if (typeof tableItem.id === "object") {
				let newKey: string;
				try {
					newKey = JSON.stringify(JSON.parse(key));
				} catch (_error) {
					newKey = key;
				}
				return JSON.stringify(tableItem.id).toLowerCase() === newKey.toLowerCase();
			} else {
				return tableItem.id.toLowerCase() === key.toLowerCase();
			}
		});
	}

	isSelected(tableItem) {
		if (this.state.tableItemSelected) {
			if (tableItem.id.string_key) {
				return tableItem.id.string_key === this.state.tableItemSelected.id.string_key;
			} else if (typeof tableItem.id === "object") {
				return JSON.stringify(tableItem.id) === JSON.stringify(this.state.tableItemSelected.id);
			} else {
				return tableItem.id === this.state.tableItemSelected.id;
			}
		} else {
			return false;
		}
	}

	handleOpenDialogClick(isDuplicate, selectedTableItem = null) {
		this.setState((prevState) => {
			const tableItem = selectedTableItem || prevState.tableItemSelected; // eslint-disable-line @typescript-eslint/prefer-nullish-coalescing
			const currentTableItem = isDuplicate ? tableItem : prevState.tableItems[0];
			let newKey = "";
			let newKeyType = "string";
			if (currentTableItem?.id.string_key) {
				newKey = currentTableItem.id.string_key;
				newKeyType = typeof currentTableItem.id.string_key;
			} else if (currentTableItem?.id) {
				newKey = JSON.stringify(currentTableItem.id, null, 2);
				newKeyType = typeof currentTableItem.id;
			}

			return {
				open: true,
				newKey: (this.props.tableId === "rocid_dict") ? this.getValidKey(newKey) : newKey,
				newKeyType: newKeyType,
				isDuplicate: isDuplicate,
				tableItemSelected: tableItem,
			};
		});
	}

	handleDialogCloseClick(tableItem) {
		this.setState((prevState) => ({
			newKey: prevState.newKey,
			newKeyType: prevState.newKeyType,
			open: false,
			showRemoveDialog: false,
			tableItemSelected: (tableItem?.id?.string_key) ? tableItem : prevState.tableItemSelected,
		}));
	}

	handleAddClick(newKey, templateJson) {
		let id;
		try {
			id = JSON.parse(newKey);
		} catch (_error) {
			id = newKey;
		}
		let data = [{
			id: id,
			data: templateJson ? this.props.tableId === "rocid_dict" ? { ...templateJson, device_info: "hide" } : templateJson : {},
		}];
		if (this.state.isDuplicate) {
			const obj = structuredClone(this.state.tableItemSelected);
			obj.id = id;
			if (this.props.tableId === "rocid_dict") {
				obj.data.rocid = id;
			}
			data = [obj];
		}
		const cmd = {
			action: "updateTable",
			tableName: this.props.tableId,
			data: data,
		} as const satisfies CmdUpdateTable;
		Gupport.send(cmd, (error, msg) => {
			if (!error && msg?.payload.status === "ok") {
				const tableItems = TableEditor.sortTableItems(msg.payload.data);
				const tableItemSelected = msg.payload.data.find((item) => (
					item.id.string_key ? item.id.string_key === newKey : compareObjects(item.id, id)
				));
				this.setState({
					// saving: false,
					error: null,
					tableItems: tableItems,
					rocIdPrefixes: (this.props.tableId === "rocid_dict") ? this.getRocIdPrefix(tableItems) : {},
					tableItemSelected: tableItemSelected,
					open: false,
					newKey: "",
					newKeyType: "",
				});
				if (this.props.tableId === "rocid_dict") {
					void this.props.navigate(`/teditor/${this.props.tableId}/${tableItemSelected.id.string_key}`);
				}
			} else {
				this.setState({
					// saving: false,
					error: error ?? msg?.payload.data,
					tableItems: [],
					rocIdPrefixes: {},
					tableItemSelected: null,
					open: false,
					newKey: "",
					newKeyType: "",
				});
			}
		});
	}

	handleDeleteClick(tableItem) {
		this.setState({
			showRemoveDialog: true,
			tableItemSelected: tableItem,
		});
	}

	handleRemoveClick() {
		const cmd = {
			action: "deleteTable",
			tableName: this.props.tableId,
			data: [{
				id: this.state.tableItemSelected.id.string_key ?? this.state.tableItemSelected.id,
			}],
		} as const satisfies CmdDeleteTable;
		Gupport.send(cmd, (error, msg) => {
			if (!error && msg?.payload.status === "ok") {
				const tableItems = TableEditor.sortTableItems(msg.payload.data);
				this.setState({
					// saving: false,
					error: null,
					tableItems: tableItems,
					rocIdPrefixes: (this.props.tableId === "rocid_dict") ? this.getRocIdPrefix(tableItems) : {},
					tableItemSelected: null,
					showRemoveDialog: false,
				});
			} else {
				this.setState({
					// saving: false,
					error: error ?? msg?.payload.data,
					tableItems: [],
					rocIdPrefixes: {},
					tableItemSelected: null,
					showRemoveDialog: false,
				});
			}
		});
	}

	getSecondaryText(tableItem) {
		return (
			Object.keys(tableItem.id).map((key) => (
				<span key={key} style={{ display: "flex", marginLeft: "18px" }}>
					<span style={{ textAlign: "left", width: "30%", fontWeight: 500 }}>{key}</span>
					<span style={{ wordWrap: "break-word", whiteSpace: "normal" }}>{String(tableItem.id[key])}</span>
				</span>
			))
		);
	}

	handleFilterChange(value) {
		this.setState({
			filterValue: value,
		});
		if (this.props.tableId === "rocid_dict") {
			Storage.set(StorageKeys.rocIdFilterQuery, value);
		}
	}

	getFilteredTableItems() {
		if (this.state.filterValue === "") {
			return this.state.tableItems;
		}

		let filterValue;
		if (this.state.searchByRegex) {
			try {
				filterValue = new RegExp(this.state.filterValue);
			} catch (_error) {
				return [];
			}
		} else {
			filterValue = this.state.filterValue.toLowerCase();
		}

		return this.state.tableItems.filter((item) => {
			let found = false;
			if (item.id.hasOwnProperty("string_key")) {
				found = this.filterTest(item.id.string_key, filterValue);
			} else {
				for (const key in item.id) {
					if (item.id.hasOwnProperty(key)) {
						if (typeof item.id[key] === "string" && this.filterTest(item.id[key], filterValue)) {
							found = true;
							break;
						}
					}
				}
			}
			if (!found && this.state.searchData) {
				found = this.filterTest(JSON.stringify(item.data), filterValue);
			}
			return found;
		});
	}

	filterTest(value, filterValue) {
		return this.state.searchByRegex ? filterValue.test(value) : value.toLowerCase().includes(filterValue);
	}

	handleSearchByRegexToggle(event, isInputChecked) {
		this.setState({
			searchByRegex: isInputChecked,
		});
		Storage.set(StorageKeys.metadataEditorSearchByRegex, isInputChecked);
	}

	handleSearchInDataToggle(event, isInputChecked) {
		this.setState({
			searchData: isInputChecked,
		});
		Storage.set(StorageKeys.metadataEditorSearchAll, isInputChecked);
	}

	override render() {
		const { t } = this.props;

		if (this.state.error) {
			return (<h3>{t("generic.errorWithMsg", { msg: this.state.error.message })}</h3>);
		}

		const dynamicHeight = this.state.containerHeight - 36 - 46 - (this.props.hasWriteAccess ? 0 : 60);

		const filteredTableItems = this.getFilteredTableItems();

		return (
			<>
				{(this.props.tableId === "rocid_dict") ?
					<div
						ref={this.#refContainerElement}
						style={{ display: "flex", flexDirection: "column", height: this.state.containerHeight }}
					>
						{this.state.loading ?
							<CircularProgress />
							:
							<>
								<div style={{ display: "flex", alignItems: "center" }}>
									<TableEditorActions
										isCustomEditor={true}
										hasWriteAccess={this.props.hasWriteAccess}
										tableItemSelected={this.state.tableItemSelected}
										onClickHandler={this.handleOpenDialogClick}
										tableItems={this.state.tableItems}
										tableId={this.props.tableId}
										loading={this.state.loading}
									/>
								</div>
								<Paper style={{ width: "100%" }}>
									<TableEditorFilter
										isRocidDict={true}
										filterValue={this.state.filterValue}
										searchAll={this.state.searchData}
										searchByRegex={this.state.searchByRegex}
										onFilterChange={this.handleFilterChange}
										onSearchByRegexToggle={this.handleSearchByRegexToggle}
										onSearchInDataToggle={this.handleSearchInDataToggle}
										searchResultLength={filteredTableItems.length}
									/>
									<TableEditorAdvanced
										hasWriteAccess={this.props.hasWriteAccess}
										filteredData={filteredTableItems}
										onDuplicateClickHandler={this.handleOpenDialogClick}
										onDeleteClick={this.handleDeleteClick}
									/>
								</Paper>
							</>
						}
					</div>
					:
					<div
						ref={this.#refContainerElement}
						style={{ display: "flex", flexDirection: "row", flexWrap: "wrap", marginTop: 5, height: this.state.containerHeight }}
					>
						<div style={{ width: "34%", paddingRight: "16px" }}>
							<TableEditorActions
								hasWriteAccess={this.props.hasWriteAccess}
								tableItemSelected={this.state.tableItemSelected}
								onClickHandler={this.handleOpenDialogClick}
								tableItems={this.state.tableItems}
								tableId={this.props.tableId}
								loading={this.state.loading}
							/>
							<Paper>
								<TableEditorFilter
									isRocidDict={false}
									filterValue={this.state.filterValue}
									searchAll={this.state.searchData}
									searchByRegex={this.state.searchByRegex}
									onFilterChange={this.handleFilterChange}
									onSearchByRegexToggle={this.handleSearchByRegexToggle}
									onSearchInDataToggle={this.handleSearchInDataToggle}
									searchResultLength={filteredTableItems.length}
								/>
								{this.state.loading ? <CircularProgress /> :
									<List style={{ overflow: "auto", padding: 0, height: dynamicHeight }}>
										{filteredTableItems.map((tableItem, index) => (
											<ListItemButton
												key={index}
												selected={this.isSelected(tableItem)}
												sx={this.isSelected(tableItem) ? { backgroundColor: (theme) => (theme.vars.palette.action.selected) } : {}}
												onClick={this.handleSelectableListChange.bind(this, tableItem)}
											>
												<ListItemText
													primary={tableItem.id._description ?? tableItem.id.string_key ?? t("teditor.objectTitle")}
													secondary={this.getSecondaryText(tableItem)}
												/>
											</ListItemButton>
										))}
									</List>
								}
							</Paper>
						</div>
						<TableEditorRight
							tableId={this.props.tableId}
							hasWriteAccess={this.props.hasWriteAccess}
							tableItemSelected={this.state.tableItemSelected}
							containerHeight={this.state.containerHeight}
							onItemUpdate={this.hanldeTableItemUpdate}
							onRemoveClick={this.openRemoveDialog}
						/>
					</div>
				}
				{this.state.open &&
					<AddNewEntryDialog
						newKey={this.state.newKey}
						newKeyType={this.state.newKeyType}
						rocIdPrefixes={this.state.rocIdPrefixes}
						isDuplicate={this.state.isDuplicate}
						checkForExistingKeys={this.checkForExistingKeys}
						isRocIdDict={this.props.tableId === "rocid_dict"}
						onAddClick={this.handleAddClick}
						onCloseHandler={this.handleDialogCloseClick}
					/>
				}
				<DeleteDialog
					open={this.state.showRemoveDialog}
					handleClose={this.handleDialogCloseClick}
					handleRemoveClick={this.handleRemoveClick}
				/>
			</>
		);
	}

}

export default withTranslation()(withNavigateAndParams(TableEditor));
